import { HttpClient } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  DataConvention,
  DimensionUnit,
  WeightUnit,
} from '../models/organizations/data-convention';
import {
  DataConventionCreatePayload,
  OrganizationDataConvention,
} from '../models/organizations/organization-data-convention';

interface LatestDataConventionResponse {
  dataConvention: DataConvention | null;
}

// Backend enums serialise as PascalCase (`Mm`, `Kg`) but the frontend types are
// lowercase to match the mat-option / rendered form-control values. Normalise
// unit fields on read so patched form controls match their option values.
const normaliseUnitsFromApi = (dc: DataConvention): DataConvention => ({
  ...dc,
  dimensionUnit: dc.dimensionUnit.toLowerCase() as DimensionUnit,
  weightUnit: dc.weightUnit.toLowerCase() as WeightUnit,
});

interface OrganizationDataConventionResponse {
  dataConvention: OrganizationDataConvention;
}

@Injectable({
  providedIn: 'root',
})
export class DataConventionsService {
  constructor(private http: HttpClient) {}

  getLatestDataConvention(organizationId: string): Observable<DataConvention | null> {
    const url: string = `${environment.apiBase}/organizations/${organizationId}/data-conventions/latest`;
    if (isDevMode()) {
      console.log('[DEV] [DataConventionsService] loading latest data convention', organizationId);
    }
    return this.http.get<LatestDataConventionResponse>(url).pipe(
      map((res: LatestDataConventionResponse) =>
        res.dataConvention === null ? null : normaliseUnitsFromApi(res.dataConvention),
      ),
    );
  }

  createDataConvention(
    organizationId: string,
    payload: DataConventionCreatePayload,
  ): Observable<OrganizationDataConvention> {
    const url: string = `${environment.apiBase}/organizations/${organizationId}/data-conventions`;
    if (isDevMode()) {
      console.log('[DEV] [DataConventionsService] creating data convention', organizationId);
    }
    return this.http
      .post<OrganizationDataConventionResponse>(url, payload)
      .pipe(map((res: OrganizationDataConventionResponse) => res.dataConvention));
  }
}
